import { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';

// ─── Icons ─────────────────────────────────────────────────────────────────────

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
  </svg>
);

const InstagramIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

// ─── Reusable URL input row ────────────────────────────────────────────────────

function UrlField({ label, icon, value, onChange, placeholder }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="flex items-center gap-2 font-poppins font-semibold text-white text-sm">
        <span style={{ color: '#F7941D' }}>{icon}</span>
        {label}
      </label>
      <p className="font-poppins text-white/40 text-xs -mt-1">
        Full URL including https://
      </p>
      <input
        type="url"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="font-poppins text-sm text-white placeholder-white/20 rounded-xl px-4 py-3 focus:outline-none transition-colors duration-200"
        style={{
          backgroundColor: '#2A2A2A',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
        onFocus={e => e.target.style.borderColor = 'rgba(247,148,29,0.5)'}
        onBlur={e => e.target.style.borderColor = 'rgba(255,255,255,0.1)'}
      />
    </div>
  );
}

// ─── Main section ──────────────────────────────────────────────────────────────

function SocialLinksSection() {
  const [facebook, setFacebook]     = useState('');
  const [instagram, setInstagram]   = useState('');
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error'

  // ── Fetch on mount ───────────────────────────────────────────────────────────
  useEffect(() => {
    async function fetchLinks() {
      setLoading(true);
      const { data, error } = await supabase
        .from('settings')
        .select('key, value')
        .in('key', ['facebook_url', 'instagram_url']);

      if (!error && data) {
        data.forEach(row => {
          if (row.key === 'facebook_url')  setFacebook(row.value ?? '');
          if (row.key === 'instagram_url') setInstagram(row.value ?? '');
        });
      }
      setLoading(false);
    }
    fetchLinks();
  }, []);

  // ── Save both rows ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);

    const [fbResult, igResult] = await Promise.all([
      supabase.from('settings').update({ value: facebook }).eq('key', 'facebook_url'),
      supabase.from('settings').update({ value: instagram }).eq('key', 'instagram_url'),
    ]);

    const failed = fbResult.error || igResult.error;
    setSaving(false);
    setSaveStatus(failed ? 'error' : 'success');
    if (!failed) setTimeout(() => setSaveStatus(null), 4000);
  };

  // ── Loading skeleton ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex flex-col gap-6 animate-pulse">
        <div>
          <div className="h-7 w-40 rounded-lg bg-white/10 mb-2" />
          <div className="h-4 w-64 rounded bg-white/5" />
        </div>
        <div className="h-52 rounded-xl bg-white/5" />
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-8 max-w-2xl">

      {/* Header */}
      <div>
        <h1 className="font-poppins font-bold text-white text-2xl">Social Links</h1>
        <p className="font-poppins text-white/40 text-sm mt-1">
          These links appear in the footer and header of your website.
        </p>
      </div>

      {/* Card */}
      <div
        className="rounded-2xl flex flex-col gap-7 p-7"
        style={{ backgroundColor: '#202020', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <UrlField
          label="Facebook URL"
          icon={<FacebookIcon />}
          value={facebook}
          onChange={setFacebook}
          placeholder="https://www.facebook.com/yourpage"
        />

        <div style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.06)' }} />

        <UrlField
          label="Instagram URL"
          icon={<InstagramIcon />}
          value={instagram}
          onChange={setInstagram}
          placeholder="https://www.instagram.com/yourhandle"
        />
      </div>

      {/* Save row */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="font-poppins font-bold text-sm px-8 py-3 rounded-xl transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: saving ? 'rgba(247,148,29,0.4)' : '#F7941D',
            color: '#1A1A1A',
            cursor: saving ? 'not-allowed' : 'pointer',
            minWidth: 120,
          }}
          onMouseEnter={e => { if (!saving) e.currentTarget.style.backgroundColor = '#ffb83f'; }}
          onMouseLeave={e => { if (!saving) e.currentTarget.style.backgroundColor = '#F7941D'; }}
        >
          {saving ? 'Saving…' : 'Save Changes'}
        </button>

        {saveStatus === 'success' && (
          <div className="flex items-center gap-2">
            <span style={{ color: '#4ade80', fontSize: 16 }}>✓</span>
            <p className="font-poppins text-sm font-medium" style={{ color: '#4ade80' }}>
              Saved successfully!
            </p>
          </div>
        )}
        {saveStatus === 'error' && (
          <div className="flex items-center gap-2">
            <span style={{ color: '#f87171', fontSize: 16 }}>✕</span>
            <p className="font-poppins text-sm font-medium" style={{ color: '#f87171' }}>
              Something went wrong. Please try again.
            </p>
          </div>
        )}
      </div>

    </div>
  );
}

export default SocialLinksSection;
